/**
 * Application defaults, advanced files, and the destructive reset controls.
 *
 * @module
 */
import { resolveAndPersistPreferredEditor } from "../../editorPreferences";
import { isElectron } from "../../env";
import { readEnvironmentApi } from "../../environmentApi";
import { useSettings, useUpdateSettings } from "../../hooks/useSettings";
import { useTheme } from "../../hooks/useTheme";
import { newCommandId } from "../../lib/utils";
import { ensureLocalApi, readLocalApi } from "../../localApi";
import { useServerAvailableEditors, useServerKeybindingsConfigPath } from "../../rpc/serverState";
import { selectProjectsAcrossEnvironments, useStore } from "../../store";
import { destroyCmmdRuntimeForEnvironment } from "../sidebar/useSidebarProjectItem";
import { Button } from "../ui/button";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { toastManager } from "../ui/toast";
import { DangerZoneSection } from "./DangerZoneSection";
import { NotificationSettings } from "./NotificationSettings";
import { ThemeSelectControl } from "./ThemeSelectControl";
import { SettingResetButton, SettingsRow, SettingsSection } from "./settingsLayout";
import { useSettingsRestore } from "./useSettingsRestore";
import { DEFAULT_UNIFIED_SETTINGS } from "@cmmd-forge/contracts/settings";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";

const TIMESTAMP_FORMAT_LABELS = {
  locale: "System default",
  "12-hour": "12-hour",
  "24-hour": "24-hour",
} as const;

/**
 * Application defaults, the advanced files, and the destructive controls.
 *
 * Observability, runtime diagnostics and the version block used to render here
 * as well. They still exist — as `SystemSection` and `AboutSection` — but this
 * page now composes all three, and rendering them from here too would show each
 * one twice. Two separate pages hid that duplication; one page surfaces it.
 */
// moved verbatim from the 2909-line ProviderSettings.tsx; complexity is
// unchanged, and fallow reads a relocated function as a new one
// fallow-ignore-next-line complexity
export function GeneralSection() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const settings = useSettings();
  const { resetSettings, updateSettings } = useUpdateSettings();
  const { changedSettingLabels, restoreDefaults } = useSettingsRestore();
  const [isHardResetting, setIsHardResetting] = useState(false);
  const [openingPathByTarget, setOpeningPathByTarget] = useState({
    keybindings: false,
    logsDirectory: false,
  });
  const [openPathErrorByTarget, setOpenPathErrorByTarget] = useState<
    Partial<Record<"keybindings" | "logsDirectory", string | null>>
  >({});
  const keybindingsConfigPath = useServerKeybindingsConfigPath();
  const availableEditors = useServerAvailableEditors();
  const openInPreferredEditor = useCallback(
    (target: "keybindings" | "logsDirectory", path: string | null, failureMessage: string) => {
      if (!path) return;
      setOpenPathErrorByTarget((existing) => ({ ...existing, [target]: null }));
      setOpeningPathByTarget((existing) => ({ ...existing, [target]: true }));

      const editor = resolveAndPersistPreferredEditor(availableEditors ?? []);
      if (!editor) {
        setOpenPathErrorByTarget((existing) => ({
          ...existing,
          [target]: "No available editors found.",
        }));
        setOpeningPathByTarget((existing) => ({ ...existing, [target]: false }));
        return;
      }

      void ensureLocalApi()
        .shell.openInEditor(path, editor)
        .catch((error) => {
          setOpenPathErrorByTarget((existing) => ({
            ...existing,
            [target]: error instanceof Error ? error.message : failureMessage,
          }));
        })
        .finally(() => {
          setOpeningPathByTarget((existing) => ({ ...existing, [target]: false }));
        });
    },
    [availableEditors],
  );

  const openKeybindingsFile = useCallback(() => {
    openInPreferredEditor("keybindings", keybindingsConfigPath, "Unable to open keybindings file.");
  }, [keybindingsConfigPath, openInPreferredEditor]);

  const openKeybindingsError = openPathErrorByTarget.keybindings ?? null;
  const isOpeningKeybindings = openingPathByTarget.keybindings;

  const handleHardReset = useCallback(async () => {
    const api = readLocalApi();
    const confirmed = await (api ?? ensureLocalApi()).dialogs.confirm(
      [
        "Hard reset Forge Desktop?",
        "This will delete ALL local projects and threads, clear all settings, and remove saved connections. This cannot be undone.",
      ].join("\n"),
    );
    if (!confirmed) return;

    setIsHardResetting(true);
    try {
      const allProjects = selectProjectsAcrossEnvironments(useStore.getState());
      await Promise.allSettled(
        allProjects.map(async (project) => {
          const projectApi = readEnvironmentApi(project.environmentId);
          if (!projectApi) return;
          await destroyCmmdRuntimeForEnvironment(project.environmentId);
          await projectApi.orchestration.dispatchCommand({
            type: "project.delete",
            commandId: newCommandId(),
            projectId: project.id,
          });
        }),
      );
      resetSettings();
      await ensureLocalApi().persistence.setSavedEnvironmentRegistry([]);
      void navigate({ to: "/", replace: true });
    } catch (error) {
      toastManager.add({
        type: "error",
        title: "Hard reset failed",
        description: error instanceof Error ? error.message : "Unknown error during reset.",
      });
    } finally {
      setIsHardResetting(false);
    }
  }, [navigate, resetSettings]);

  return (
    <>
      <SettingsSection id="general" title="Settings">
        <NotificationSettings />
        <SettingsRow
          title="Theme"
          description="Follow the system appearance or choose a fixed theme for this device."
          resetAction={
            theme !== DEFAULT_UNIFIED_SETTINGS.theme ? (
              <SettingResetButton
                label="theme"
                onClick={() => setTheme(DEFAULT_UNIFIED_SETTINGS.theme)}
              />
            ) : null
          }
          control={<ThemeSelectControl theme={theme} onThemeChange={setTheme} />}
        />

        <SettingsRow
          title="Time format"
          description="System default follows your browser or OS clock preference."
          resetAction={
            settings.timestampFormat !== DEFAULT_UNIFIED_SETTINGS.timestampFormat ? (
              <SettingResetButton
                label="time format"
                onClick={() =>
                  updateSettings({
                    timestampFormat: DEFAULT_UNIFIED_SETTINGS.timestampFormat,
                  })
                }
              />
            ) : null
          }
          control={
            <Select
              value={settings.timestampFormat}
              onValueChange={(value) => {
                if (value === "locale" || value === "12-hour" || value === "24-hour") {
                  updateSettings({ timestampFormat: value });
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-40" aria-label="Timestamp format">
                <SelectValue>{TIMESTAMP_FORMAT_LABELS[settings.timestampFormat]}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                <SelectItem hideIndicator value="locale">
                  {TIMESTAMP_FORMAT_LABELS.locale}
                </SelectItem>
                <SelectItem hideIndicator value="12-hour">
                  {TIMESTAMP_FORMAT_LABELS["12-hour"]}
                </SelectItem>
                <SelectItem hideIndicator value="24-hour">
                  {TIMESTAMP_FORMAT_LABELS["24-hour"]}
                </SelectItem>
              </SelectPopup>
            </Select>
          }
        />
      </SettingsSection>

      {isElectron ? (
        <SettingsSection title="Advanced">
          <SettingsRow
            title="Keybindings"
            description="Open the persisted `keybindings.json` file to edit advanced bindings directly."
            status={
              <>
                <span className="block break-all font-mono text-[11px] text-foreground">
                  {keybindingsConfigPath ?? "Resolving keybindings path..."}
                </span>
                {openKeybindingsError ? (
                  <span className="mt-1 block text-destructive">{openKeybindingsError}</span>
                ) : (
                  <span className="mt-1 block">Opens in your preferred editor.</span>
                )}
              </>
            }
            control={
              <Button
                size="xs"
                variant="outline"
                disabled={!keybindingsConfigPath || isOpeningKeybindings}
                onClick={openKeybindingsFile}
              >
                {isOpeningKeybindings ? "Opening..." : "Open file"}
              </Button>
            }
          />
        </SettingsSection>
      ) : null}

      <DangerZoneSection
        changedSettingLabels={changedSettingLabels}
        isHardResetting={isHardResetting}
        onRestoreDefaults={() => void restoreDefaults()}
        onHardReset={() => void handleHardReset()}
      />
    </>
  );
}
